/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : requestAccess.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import React from "react";
import PropTypes from "prop-types";
import CustomTabsV2 from "../../components/customComponentsV2/common/tabs/CustomTabsV2";
import CustomFormTextFieldV2 from "../../components/customComponentsV2/common/textField/CustomFormTextFieldV2";
import CustomButtonV2 from "../../components/customComponentsV2/common/button/CustomButtonV2";
import appTheme from "../../assets/app-theme/appTheme";

function RequestsAndLogsTabs({
  getRequestsData = [],
  getStatusLabel,
  reason = "",
  setReason,
  reasonError,
  setReasonError,
  handleSubmit,
  onClose,
}) {
  return (
    <Box sx={{ width: "100%" }}>
      <CustomTabsV2
        tabs={[
          {
            name: "Requests",
            content:
              getRequestsData?.[0]?.status === 100 ? (
                "Request already sent and pending for approval"
              ) : (
                <Box sx={{ p: 2 }}>
                  <Typography fontWeight={600} mb={1}>
                    Request Access
                  </Typography>

                  <CustomFormTextFieldV2
                    size="small"
                    required={true}
                    label="Reason"
                    type="text"
                    fldType="text5"
                    value={reason}
                    setValue={(value) =>
                      value?.length <= 100 && setReason?.(value)
                    }
                    valueError={reasonError}
                    setValueError={setReasonError}
                    maxLength={100}
                  />

                  <Box
                    sx={{
                      display: "flex",
                      gap: 1,
                      justifyContent: "flex-end",
                      mt: 2,
                    }}
                  >
                    <CustomButtonV2
                      handleOnClick={onClose}
                      sx={{ ...appTheme.commonBtnStyle }}
                      btnName="Close"
                      type="cancel"
                    />
                    <CustomButtonV2
                      handleOnClick={handleSubmit}
                      disabled={!reason || reason.length < 5}
                      btnName="Send"
                      type="submit"
                    />
                  </Box>
                </Box>
              ),
          },
          {
            name: "Logs",
            content:
              getRequestsData?.length > 0 ? (
                <TableContainer component={Paper} sx={{ mb: 3 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>
                          <strong>S.No</strong>
                        </TableCell>
                        <TableCell>
                          <strong>Reason</strong>
                        </TableCell>
                        <TableCell>
                          <strong>Status</strong>
                        </TableCell>
                        <TableCell>
                          <strong>Requestor</strong>
                        </TableCell>
                        <TableCell>
                          <strong>Created At</strong>
                        </TableCell>
                        <TableCell>
                          <strong>Updated At</strong>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {getRequestsData.map((request, index) => (
                        <TableRow
                          key={
                            request?.id ||
                            request?.request_id ||
                            request?.created_at ||
                            request?.request_reason
                          }
                        >
                          <TableCell>{index + 1}</TableCell>
                          <TableCell>{request?.request_reason}</TableCell>
                          <TableCell>
                            {getStatusLabel
                              ? getStatusLabel(request?.status)
                              : request?.status}
                          </TableCell>
                          <TableCell>
                            {request?.requested_user?.fullname || "-"}
                          </TableCell>
                          <TableCell>
                            {request?.created_at
                              ? new Date(request.created_at).toLocaleString()
                              : "-"}
                          </TableCell>
                          <TableCell>
                            {request?.updated_at
                              ? new Date(request.updated_at).toLocaleString()
                              : "-"}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography>No requests available.</Typography>
              ),
          },
        ]}
      />
    </Box>
  );
}

RequestsAndLogsTabs.propTypes = {
  getRequestsData: PropTypes.arrayOf(PropTypes.object),
  getStatusLabel: PropTypes.func,
  reason: PropTypes.string,
  setReason: PropTypes.func,
  reasonError: PropTypes.string,
  setReasonError: PropTypes.func,
  handleSubmit: PropTypes.func,
  onClose: PropTypes.func,
};

export default RequestsAndLogsTabs;
